import { useEffect, useRef, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { alert } from 'equasion-lib';
import downloadFile from '@/lib/utils/downloadFile';
import { operatorFacingMessage } from '@/lib/utils/errorUtils';
import { useRxPrintEnabled, useSendDocumentsToPrinter } from '@/lib/rxPrint';
import { packDocumentsReady } from '../../verifyScreen';
import PackDocumentsPanel, { type PackDocumentsPanelProps } from './PackDocumentsPanel';

/**
 * The documents printed at Verify (design D14 / §11.7): vendor-only `pack-invoice` /
 * `pack-packing-list` Documents the jobs server files a few seconds after Verify completes (or
 * after the Verify-stage split). The Order fields return null for anyone who is not reading as the
 * vendor, and exclude archived copies — Reopen Verify archives them, so a reopened-then-reverified
 * order never shows the old box paperwork.
 */
export const SALES_ORDER_PACK_DOCUMENTS = gql`
  query SalesOrderPackDocuments($id: ID!, $vendorId: ID!) {
    salesOrder(id: $id, vendorId: $vendorId) {
      id
      vendorSalesOrderNumber
      vendorStatus
      packInvoiceDocument(companyId: $vendorId) {
        id
        filename
        humanFilename
        link
      }
      packPackingListDocument(companyId: $vendorId) {
        id
        filename
        humanFilename
        link
      }
      packStickersDocument(companyId: $vendorId) {
        id
        filename
        humanFilename
        link
      }
    }
  }
`;

export const SALES_ORDER_PACK_DOCUMENTS_GENERATE = gql`
  mutation SalesOrderPackDocumentsGenerate($companyId: ID!, $salesOrderId: ID!) {
    salesOrderPackDocumentsGenerate(companyId: $companyId, salesOrderId: $salesOrderId)
  }
`;

/**
 * Print All: the server merges the order's current packing list and invoice into one PDF (packing
 * list first) and answers with its signed link. Made on the tap, not ahead of it — the server reads
 * the newest active copies then, so a Regenerate in between is never printed stale.
 */
export const SALES_ORDER_PACK_DOCUMENTS_PRINT_ALL = gql`
  mutation SalesOrderPackDocumentsPrintAll($companyId: ID!, $salesOrderId: ID!) {
    salesOrderPackDocumentsPrintAll(companyId: $companyId, salesOrderId: $salesOrderId) {
      link
      humanFilename
    }
  }
`;

/** Poll every 2 s while a document is missing, for at most 90 s (then Check again / Regenerate). */
export const PACK_DOCUMENTS_POLL_INTERVAL_MS = 2000;
export const PACK_DOCUMENTS_POLL_WINDOW_MS = 90 * 1000;

/**
 * Print All waits at most this long for its merged PDF, the mutation and the download together.
 * The HTTP link has no timeout, so a request that never settles would otherwise hold the button on
 * "Preparing…" for the rest of the session; past this the tap is abandoned (a late answer is
 * dropped, never printed) and the operator is told to try again.
 */
export const PACK_DOCUMENTS_PRINT_ALL_DEADLINE_MS = 30 * 1000;

type PackDocumentsProps = Omit<
  PackDocumentsPanelProps,
  | 'order'
  | 'polling'
  | 'timedOut'
  | 'regenerating'
  | 'printingAll'
  | 'rxPrintEnabled'
  | 'sendingKey'
  | 'onPrint'
  | 'onPrintAll'
  | 'onCheckAgain'
  | 'onRegenerate'
> & {
  /** The verified order whose box this is (the split's verified child after a Verify split). */
  salesOrderId: string | null;
  vendorId: string;
};

function PackDocuments({
  salesOrderId,
  vendorId,
  open,
  ...panelProps
}: PackDocumentsProps) {
  const { data, refetch } = useQuery(SALES_ORDER_PACK_DOCUMENTS, {
    variables: { id: salesOrderId, vendorId },
    fetchPolicy: 'network-only',
    skip: !open || !salesOrderId,
    // Each poll must be its own request: Apollo would otherwise fold a refetch into a still-pending
    // identical one, so a single request that never settles (a handheld slept mid-request; the
    // HTTP link has no timeout) would silently swallow every later poll. Same reason as the
    // shipping Documents page.
    context: { queryDeduplication: false }
  });
  const [generate] = useMutation(SALES_ORDER_PACK_DOCUMENTS_GENERATE);
  const [printAllMutation] = useMutation(SALES_ORDER_PACK_DOCUMENTS_PRINT_ALL);
  const [timedOut, setTimedOut] = useState(false);
  const [pollRound, setPollRound] = useState(0);
  const [regenerating, setRegenerating] = useState(false);
  const [printingAll, setPrintingAll] = useState(false);
  const { send: sendToPrinter, sendingKey } = useSendDocumentsToPrinter(vendorId);
  // Each Print All tap's number: only the newest tap, not yet abandoned, may print or toast.
  const printAllAttemptRef = useRef(0);
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // Last good answer, per order: a poll that fails (errorPolicy 'none' clears data) must not turn a
  // printable document back into "Generating…".
  const lastOrderRef = useRef<any>(null);
  if (lastOrderRef.current && String(lastOrderRef.current.id) !== String(salesOrderId)) {
    lastOrderRef.current = null;
  }
  if (data?.salesOrder) lastOrderRef.current = data.salesOrder;
  const order = data?.salesOrder ?? lastOrderRef.current;
  // Read once, not on every 2 s poll of the documents (see @/lib/rxPrint).
  const rxPrintEnabled = useRxPrintEnabled(vendorId);
  const orderNumber = order?.vendorSalesOrderNumber || 'order';
  const downloadOf = (document: any, fallbackFilename: string) => () =>
    void downloadFile(document.link, document.humanFilename || fallbackFilename);
  const ready = packDocumentsReady(order);
  const polling = open && !!salesOrderId && !ready.all && !timedOut;

  useEffect(() => {
    if (!polling) return undefined;
    const poll = setInterval(() => {
      refetchRef.current().catch(() => {
        // Transient — the next tick retries.
      });
    }, PACK_DOCUMENTS_POLL_INTERVAL_MS);
    const windowTimer = setTimeout(
      () => setTimedOut(true),
      PACK_DOCUMENTS_POLL_WINDOW_MS
    );
    return () => {
      clearInterval(poll);
      clearTimeout(windowTimer);
    };
  }, [polling, pollRound]);

  const pollAgain = () => {
    setTimedOut(false);
    setPollRound((round) => round + 1);
    refetchRef.current().catch(() => {});
  };

  const regenerate = async () => {
    if (!salesOrderId || regenerating) return;
    setRegenerating(true);
    try {
      await generate({ variables: { companyId: vendorId, salesOrderId } });
      alert('Regenerating', 'The documents are being generated again.', 4, 'success');
      pollAgain();
    } catch (e) {
      alert(
        'Could not regenerate',
        operatorFacingMessage(e, 'Could not regenerate the documents. Please try again.'),
        6,
        'error'
      );
    } finally {
      setRegenerating(false);
    }
  };

  const printAll = async () => {
    if (!salesOrderId || printingAll) return;
    if (rxPrintEnabled) {
      // RxPrint: no merged PDF — each document goes to its own printer (the stickers to the label
      // printer), packing list first.
      const documents = (
        [
          [order?.packPackingListDocument, `Packing List - SO ${orderNumber}.pdf`],
          [order?.packInvoiceDocument, `Invoice - SO ${orderNumber}.pdf`],
          [order?.packStickersDocument, `stickers-${orderNumber}.zpl`]
        ] as [any, string][]
      )
        .filter(([document]) => document?.id && document?.link)
        .map(([document, fallbackFilename]) => ({
          id: document.id,
          download: downloadOf(document, fallbackFilename)
        }));
      await sendToPrinter('all', salesOrderId, documents);
      return;
    }
    const attempt = ++printAllAttemptRef.current;
    const current = () => printAllAttemptRef.current === attempt;
    // The single Print buttons are untouched by this, so they stay the way out.
    const failed = (e?: unknown) =>
      alert(
        'Could not print all documents',
        operatorFacingMessage(
          e,
          'Could not prepare the documents. Try again, or print them one at a time.'
        ),
        6,
        'error'
      );
    setPrintingAll(true);
    const deadline = setTimeout(() => {
      if (!current()) return;
      printAllAttemptRef.current += 1;
      setPrintingAll(false);
      failed();
    }, PACK_DOCUMENTS_PRINT_ALL_DEADLINE_MS);
    try {
      const { data: printed } = await printAllMutation({
        variables: { companyId: vendorId, salesOrderId }
      });
      // Answered after the deadline: the operator was already told to try again.
      if (!current()) return;
      const { link, humanFilename } = printed?.salesOrderPackDocumentsPrintAll || {};
      if (!link) throw new Error('Print All answered without a link');
      await downloadFile(
        link,
        humanFilename || `Documents - SO ${order?.vendorSalesOrderNumber || 'order'}.pdf`
      );
    } catch (e) {
      if (current()) failed(e);
    } finally {
      clearTimeout(deadline);
      if (current()) setPrintingAll(false);
    }
  };

  return (
    <PackDocumentsPanel
      {...panelProps}
      open={open}
      order={order}
      polling={polling}
      timedOut={timedOut && !ready.all}
      regenerating={regenerating}
      printingAll={printingAll || sendingKey === 'all'}
      rxPrintEnabled={rxPrintEnabled}
      sendingKey={sendingKey}
      onPrint={(document, fallbackFilename) => {
        if (!document?.link) return;
        const download = downloadOf(document, fallbackFilename);
        if (rxPrintEnabled && document.id) {
          void sendToPrinter(document.id, salesOrderId, [{ id: document.id, download }]);
        } else download();
      }}
      onPrintAll={printAll}
      onCheckAgain={pollAgain}
      onRegenerate={regenerate}
    />
  );
}

export default PackDocuments;
